import { HttpClient } from '@angular/common/http';
import { CrudService } from './crud.service';
import { Injectable, inject } from '@angular/core';
import { ShippingCarrier, ShippingCarrierType } from '../models/shipping-carrier';
import { GeneralShippingSettingsOption } from '../models/company';
@Injectable({
  providedIn: 'root',
})
export class ShippingCarrierService extends CrudService<ShippingCarrier> {
  constructor() {
    const http = inject(HttpClient);

    super(http, 'shipping-carriers');
  }

  public async getMyShippingCarriers(options?: { status?: string }): Promise<ShippingCarrier[]> {
    const params = options?.status ? { status: options.status } : {};
    const data = await this.http.get<ShippingCarrier[]>(`${this.endpoint}/`, { params }).toPromise();

    return data.map((item) => ({ ...item }));
  }

  public async getMyShippingCarriersWithOptions(options?: {
    status?: string;
  }): Promise<GeneralShippingSettingsOption[]> {
    const params = options?.status ? { status: options.status } : {};
    const data = await this.http
      .get<GeneralShippingSettingsOption[]>(`${this.endpoint}/carriers-options`, { params })
      .toPromise();

    return data.map((item) => ({ ...item }));
  }

  public async addDefaultShippingCarrier(data: AddDefaultShippingCarrierData): Promise<ShippingCarrier> {
    const result = await this.http.post<ShippingCarrier>(`${this.endpoint}/default-carrier`, data).toPromise();
    this.dataChangedSubject.next(true);
    return result;
  }

  public async getFilteredServiceCodes(carrierId: string): Promise<string[]> {
    return await this.http.get<string[]>(`${this.endpoint}/filtered-service-codes/${carrierId}`).toPromise();
  }

  public async connectStamps(username: string, password: string): Promise<ShippingCarrier> {
    const result = await this.http
      .post<ShippingCarrier>(`${this.endpoint}/connect/stamps`, { username, password })
      .toPromise();
    this.dataChangedSubject.next(true);
    return result;
  }

  public async connectEndicia(account: string, passphrase: string): Promise<ShippingCarrier> {
    const result = await this.http
      .post<ShippingCarrier>(`${this.endpoint}/connect/endicia`, { account, passphrase })
      .toPromise();
    this.dataChangedSubject.next(true);
    return result;
  }

  /**
   * CRAT-1242 — connects the seller's Royal Mail OBA account. The carrier comes back as `pending`;
   * ShipEngine verifies the credentials with Royal Mail out of band (up to 5 business days).
   */
  public async connectRoyalMail(data: ConnectRoyalMailData): Promise<ShippingCarrier> {
    const result = await this.http.post<ShippingCarrier>(`${this.endpoint}/connect/royal-mail`, data).toPromise();
    this.dataChangedSubject.next(true);
    return result;
  }

  public async connectUps(data: ConnectUpsData): Promise<ConnectUpsResponse> {
    return await this.http.post<ConnectUpsResponse>(`${this.endpoint}/connect/ups`, data).toPromise();
  }

  public async completeUpsConnection(carrierId: string): Promise<ShippingCarrier> {
    const result = await this.http
      .post<ShippingCarrier>(`${this.endpoint}/connect/ups/${carrierId}/complete`, {})
      .toPromise();
    this.dataChangedSubject.next(true);
    return result;
  }

  /** CRAT-1183 — the underlying ShipEngine carrier_code (e.g. 'fedex'), used to gate FedEx-only settings. */
  public async getCarrierProvider(carrierId: string): Promise<{ carrierCode: string | null }> {
    return await this.http.get<{ carrierCode: string | null }>(`${this.endpoint}/${carrierId}/provider`).toPromise();
  }

  /** CRAT-1183 — push FedEx ETD signature/letterhead images (raw base64) to the carrier's ShipEngine connection. */
  public async updateFedexSettings(
    carrierId: string,
    payload: { signatureImage?: string; letterheadImage?: string }
  ): Promise<{ success: true }> {
    return await this.http.put<{ success: true }>(`${this.endpoint}/${carrierId}/fedex-settings`, payload).toPromise();
  }
}

export type AddDefaultShippingCarrierData = {
  shippingCarrierCredentialsId: string;
  name: string;
  type: ShippingCarrierType;
};

export type ConnectRoyalMailData = {
  accountNumber: string;
  obaEmail: string;
  postingLocationNumber: string;
  companyName: string;
  contactName: string;
  email: string;
  streetLine1: string;
  streetLine2?: string;
  city: string;
  postalCode: string;
  phone: string;
};

export type ConnectUpsData = {
  accountNumber: string;
  accountPostalCode?: string;
  accountCountryCode?: string;
};

export type ConnectUpsResponse = {
  oauthUrl: string;
  carrierId: string;
};
